import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import {
  creatableAIProviders,
  defaultChatModel,
  defaultFastModel,
  defaultManagedAIProvider,
  defaultManagedChatModel,
  getAIProvider,
  isEffortLevel,
  MANAGED_AI_PROVIDER_ID,
} from "../../../shared/aiProviders";
import type { AiProvider as AiConfig } from "../../../shared/generatedDrizzleSchema";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useManagedAIProvider } from "../../hooks/useManagedAIProvider";
import { useQuery } from "../../hooks/useQuery";
import { getConfigProvider, getConfigSubtitle } from "../../lib/aiProvider";
import { effortLabel } from "../../lib/effort";
import {
  makeUrl,
  navigate,
  Route,
  routes,
  useCurrentUrl,
} from "../../lib/urls";
import { AiProviderModal } from "../AiProviderModal";
import { toDraft, toMutationArgs } from "../AiProviderModal/aiProviderDraft";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { ListTable } from "../ListTable";
import { ProviderLogo } from "../ProviderLogo";
import { StatusBadge } from "../StatusBadge";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { Tooltip } from "../Tooltip";
import { Cell, IdentityCell } from "./IdentityCell";
import { Container, Header, ScrollArea } from "./shared";

function getDisplayedChatModel(config: AiConfig) {
  return config.chatModel ?? defaultChatModel(getConfigProvider(config));
}

function getDisplayedFastModel(config: AiConfig) {
  return (
    config.fastModel ??
    config.chatModel ??
    defaultFastModel(getConfigProvider(config))
  );
}

function DefaultBadge() {
  return <StatusBadge color="green">Default</StatusBadge>;
}

function ProviderToggle({
  isOn,
  onToggle,
  disabled,
  tooltip,
}: {
  isOn: boolean;
  onToggle: () => void;
  disabled?: boolean;
  tooltip?: string;
}) {
  const control = (
    <div
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <Toggle isOn={isOn} onToggle={onToggle} disabled={disabled} />
    </div>
  );
  return tooltip ? <Tooltip content={tooltip}>{control}</Tooltip> : control;
}

export function AISettings() {
  const z = useZero();
  const [configs, configsResult] = useQuery(queries.aiConfigs());
  const canManageAI = useHasAccess("tenant:ai_providers_manage");
  type AiConfigRow = (typeof configs)[number];
  const draftConnectorId = (config: AiConfigRow) =>
    config.appUsage?.connectedAppId ?? null;
  const {
    managedProvider,
    locked: managedLocked,
    disabled: managedDisabled,
  } = useManagedAIProvider();
  const activeConfig = configs.find((config) => config.active) ?? null;

  const { configId: urlConfigId } = useCurrentUrl(routes.settings.ai);
  const { provider: newProvider } = useCurrentUrl(routes.settings.ai.new);
  const initialProvider = creatableAIProviders.find(
    (candidate) => candidate.id === newProvider
  )?.id;
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  function closeModal() {
    navigate(routes.settings.ai, { configId: "" }, { replace: true });
  }

  function updateConfig(config: AiConfigRow, active: boolean) {
    z.mutate(
      sharedMutators.updateAIConfig(
        toMutationArgs(
          config.id,
          active,
          toDraft(config, draftConnectorId(config))
        )
      )
    );
  }

  function setManagedDisabled(disabled: boolean) {
    z.mutate(sharedMutators.setManagedAIProviderDisabled({ disabled }));
  }

  function setProviderDisabled(id: string, disabled: boolean) {
    z.mutate(sharedMutators.setAIProviderDisabled({ id, disabled }));
  }

  function handleDelete() {
    if (confirmDelete == null) return;
    z.mutate(sharedMutators.deleteAIConfig({ id: confirmDelete.id }));
    setConfirmDelete(null);
  }

  const items = [
    ...(managedProvider == null
      ? []
      : [
          {
            key: MANAGED_AI_PROVIDER_ID,
            link: makeUrl(null, { configId: MANAGED_AI_PROVIDER_ID }),
            name: (
              <IdentityCell
                title="Managed by 3B"
                subtitle="Anthropic and OpenAI"
                icon={<Icon name="tines-mark" size={16} />}
                badge={
                  activeConfig == null && !managedDisabled ? (
                    <DefaultBadge />
                  ) : undefined
                }
              />
            ),
            enabled: (
              <ProviderToggle
                isOn={!managedDisabled}
                onToggle={() => setManagedDisabled(!managedDisabled)}
                disabled={
                  managedLocked || (activeConfig == null && !managedDisabled)
                }
                tooltip={
                  managedLocked
                    ? "Not available for this tenant"
                    : activeConfig == null && !managedDisabled
                      ? "Set another provider as the default first"
                      : undefined
                }
              />
            ),
            chatModel: <Cell value={defaultManagedChatModel} />,
            fastModel: <Cell value={defaultManagedAIProvider.fastModel} />,
            effort: (
              <Cell
                value={effortLabel(
                  getAIProvider(managedProvider).defaultChatEffort ?? null
                )}
              />
            ),
            actions: (hasMouse: boolean) => {
              // The toggle owns enable/disable; the menu only offers handing the
              // default back from a configured provider to the managed one.
              if (managedDisabled || activeConfig == null) return null;
              return (
                <div
                  style={{ opacity: hasMouse ? 1 : 0 }}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <Dropdown
                    opener={<Button variant="secondary" icon="more" />}
                    align="bottom"
                    minWidth={180}
                    sections={[
                      {
                        items: [
                          {
                            type: "button" as const,
                            left: <Icon name="star" size={16} />,
                            label: "Set as default",
                            onClick: () =>
                              activeConfig != null &&
                              updateConfig(activeConfig, false),
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              );
            },
          },
        ]),
    ...configs.map((config) => {
      const provider = getConfigProvider(config);
      return {
        key: config.id,
        link: makeUrl(null, { configId: config.id }),
        name: (
          <IdentityCell
            title={getAIProvider(provider).label}
            subtitle={getConfigSubtitle(
              config,
              config.appUsage?.connectedApp?.configuration
            )}
            icon={<ProviderLogo provider={provider} />}
            badge={config.active ? <DefaultBadge /> : undefined}
          />
        ),
        enabled: (
          <ProviderToggle
            isOn={config.disabledAt == null}
            onToggle={() =>
              setProviderDisabled(config.id, config.disabledAt == null)
            }
            disabled={config.active === true}
            tooltip={
              config.active
                ? "Set another provider as the default first"
                : undefined
            }
          />
        ),
        chatModel: <Cell value={getDisplayedChatModel(config)} />,
        fastModel: <Cell value={getDisplayedFastModel(config)} />,
        effort: (
          <Cell
            value={effortLabel(
              isEffortLevel(config.effort) ? config.effort : null
            )}
          />
        ),
        actions: (hasMouse: boolean) => (
          <div
            style={{ opacity: hasMouse ? 1 : 0 }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              align="bottom"
              minWidth={180}
              sections={[
                {
                  items: [
                    ...(config.disabledAt == null && !config.active
                      ? [
                          {
                            type: "button" as const,
                            left: <Icon name="star" size={16} />,
                            label: "Set as default",
                            onClick: () => updateConfig(config, true),
                          },
                        ]
                      : []),
                    {
                      type: "button" as const,
                      left: <Icon name="pencil" size={16} />,
                      label: "Edit",
                      onClick: () =>
                        navigate(
                          null,
                          { configId: config.id },
                          { replace: true }
                        ),
                    },
                    {
                      type: "button" as const,
                      left: <Icon name="trash" size={16} />,
                      label: "Delete",
                      onClick: () =>
                        setConfirmDelete({
                          id: config.id,
                          name: getAIProvider(provider).label,
                        }),
                    },
                  ],
                },
              ]}
            />
          </div>
        ),
      };
    }),
  ];

  if (!canManageAI) {
    return (
      <Container>
        <Header>
          <Text variant="text-regular-md">AI providers</Text>
        </Header>
        <ScrollArea>
          <div style={{ padding: 24 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Only admins can change this
            </Text>
          </div>
        </ScrollArea>
      </Container>
    );
  }

  return (
    <Container>
      <Header style={{ paddingRight: 12 }}>
        <Text variant="text-regular-md">AI providers</Text>
        <div style={{ marginLeft: "auto" }}>
          <Button
            size="l"
            icon="plus"
            onClick={() => navigate(routes.settings.ai.new, { provider: "" })}
          />
        </div>
      </Header>

      <ScrollArea>
        {items.length === 0 ? (
          configsResult.type === "complete" ? (
            <EmptyState
              iconName="brain"
              title="No AI providers"
              description="Add a provider to start building workflows in 3B."
            />
          ) : null
        ) : (
          <ListTable
            headers={{
              name: "Name",
              chatModel: "Chat model",
              fastModel: "Fast model",
              effort: "Default effort",
              enabled: "Enabled",
              actions: "",
            }}
            items={items}
          />
        )}
      </ScrollArea>

      {urlConfigId != null && (
        <AiProviderModal id={urlConfigId} onClose={closeModal} />
      )}
      <Route path={routes.settings.ai.new}>
        {() => (
          <AiProviderModal
            id={null}
            initialProvider={initialProvider}
            onClose={closeModal}
          />
        )}
      </Route>

      <ConfirmModal
        open={confirmDelete != null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete AI provider"
        description={
          confirmDelete == null
            ? ""
            : `"${confirmDelete.name}" will be removed. This can't be undone.`
        }
        confirmLabel="Delete provider"
        destructive
      />
    </Container>
  );
}
